// Todo el contenido editorial del sitio de Lizy Rodríguez.
// Textos tomados del documento original y redactados en el mismo tono psicoanalítico.

export const site = {
  name: "Lizy Rodríguez",
  role: "Psicóloga Clínica",
  specialty: "Psicoterapia Psicoanalítica",
  domain: "lizyrodriguezpsicologa.cl",
  email: "l.alejandra.rodriguez@gmail.com",
  whatsapp: "+569 3021 4624",
  whatsappRaw: "56930214624",
  location: "La Reina, a pasos del metro Fernando Castillo Velasco",
};

export const nav = [
  { label: "Inicio", to: "/" },
  { label: "La Escucha", to: "/la-escucha" },
  { label: "Sobre mí", to: "/sobre-mi" },
  { label: "Formación", to: "/formacion" },
  { label: "Cuaderno", to: "/cuaderno" },
  { label: "Primera entrevista", to: "/primera-entrevista" },
  { label: "Contacto", to: "/contacto" },
  { label: "Agenda", to: "/agenda" },
];

export const home = {
  heroLines: [
    "Hay palabras",
    "que cambian una vida.",
  ],
  heroSub:
    "No porque sean extraordinarias. Sino porque, por primera vez, encuentran un lugar donde pueden ser escuchadas.",
  heroDescription:
    "Acompaño a adolescentes, adultos y parejas en un espacio de escucha, reflexión y elaboración del sufrimiento psíquico.",
  question: "¿Y si aquello que hoy produce sufrimiento tuviera algo importante que decir?",
  manifesto:
    "La psicoterapia psicoanalítica parte de una convicción sencilla y, al mismo tiempo, profundamente transformadora: no todo aquello que orienta nuestra vida es plenamente consciente. Muchas veces creemos conocer las razones de nuestro sufrimiento y, sin embargo, algo insiste en repetirse. Es en ese punto donde la palabra adquiere un valor distinto. Hablar no consiste únicamente en contar lo que ha ocurrido. También es descubrir aquello que aparece entre los silencios, las dudas, las contradicciones y las repeticiones que atraviesan una historia. Por eso, la escucha clínica no busca confirmar lo que ya sabemos sobre nosotros mismos. Busca abrir un espacio donde pueda aparecer algo nuevo.",
  pullQuote:
    "Escuchar no es buscar respuestas. Es hacer posible que aparezcan nuevas preguntas.",
  aboutTeaser:
    "Desde el 2010 trabajando en clínica escuchando historias singulares. Trabajo desde el psicoanálisis contemporáneo, atenta a la particularidad de cada persona y a los vínculos que nos constituyen.",
};

export const pillars = [
  {
    number: "01",
    title: "Escucha",
    text: "Cada historia merece una escucha singular.",
  },
  {
    number: "02",
    title: "Palabra",
    text: "La palabra puede abrir nuevas posibilidades cuando encuentra un lugar donde ser escuchada.",
  },
  {
    number: "03",
    title: "Tiempo",
    text: "Cada proceso tiene su propio ritmo.",
  },
];

export const laEscucha = {
  intro: "Una forma de comprender el sufrimiento humano.",
  sections: [
    {
      title: "La escucha",
      body: [
        "Escuchar parece algo simple, y sin embargo pocas veces en la vida tenemos la experiencia de ser realmente escuchados. La escucha clínica no consiste únicamente en atender a lo que una persona dice: también supone prestar atención a aquello que aparece entre las palabras —los silencios, las repeticiones, las emociones y las preguntas que todavía no encuentran una forma clara de expresarse.",
        "No se trata de confirmar lo que ya sabemos sobre nosotros mismos, sino de abrir un espacio donde pueda aparecer algo nuevo. Un lugar donde no es necesario justificar lo que se siente, donde las contradicciones pueden existir y donde lo que parece confuso puede ser explorado sin apuro.",
      ],
    },
    {
      title: "La palabra",
      body: [
        "No toda palabra produce los mismos efectos. Hablar no consiste únicamente en transmitir información acerca de uno mismo: a veces, en el acto mismo de poner una experiencia en palabras, algo nuevo comienza a revelarse.",
        "Las palabras no cambian el pasado, pero pueden transformar la relación que mantenemos con él. Cuando una experiencia encuentra palabras y una escucha disponible para recibirlas, aquello que permanecía aislado, confuso o doloroso puede comenzar a adquirir nuevos sentidos.",
      ],
    },
    {
      title: "La singularidad",
      body: [
        "Cada persona es única, y el trabajo psicoterapéutico requiere una escucha atenta a esa singularidad más que la aplicación de respuestas generales o estandarizadas. No hay recetas ni pasos definidos que sirvan para todos por igual.",
        "En el psicoanálisis considero tanto la historia personal como la importancia de los vínculos en la construcción de quienes somos. Por eso, comprender el sufrimiento supone atender a la manera particular en que cada historia se ha ido escribiendo.",
      ],
    },
    {
      title: "El encuentro psicoterapéutico",
      body: [
        "La psicoterapia es un proceso que se construye en conjunto. No se trata de recibir consejos ni respuestas prefabricadas, sino de sostener un encuentro donde la propia experiencia pueda ser pensada, reconocida y elaborada.",
        "Es en ese encuentro donde algo comienza a modificarse: lo que aparecía como una vivencia incomprensible puede empezar a integrarse a la propia historia, abriendo nuevas posibilidades de comprensión y transformación.",
      ],
    },
  ],
  closing:
    "Cada historia merece una escucha que esté a la altura de su singularidad.",
};

export const sobreMi = {
  title: "¿Por qué elegí el psicoanálisis?",
  intro: [
    "Creo que muchas veces el sufrimiento aparece cuando nos encontramos repitiendo experiencias, emociones o formas de relacionarnos que no logramos comprender del todo. A veces se expresa como ansiedad, tristeza, sensación de vacío, dificultades en los vínculos o una persistente sensación de estar desconectados de nosotros mismos. Mi trabajo consiste en ofrecer un espacio donde aquello pueda ser pensado y escuchado sin juicios ni respuestas prefabricadas. Un espacio para detenerse, explorar la propia experiencia y encontrar nuevas formas de comprender aquello que duele.",
    "Mi mirada clínica se inspira en el psicoanálisis contemporáneo y en una comprensión de la subjetividad que considera tanto la historia personal como la importancia de los vínculos en la construcción de quienes somos. Entiendo que cada persona es única y que el trabajo terapéutico requiere una escucha atenta a esa singularidad, más que la aplicación de respuestas generales o estandarizadas.",
    "Mantengo un compromiso permanente con mi formación clínica y teórica, participando en espacios de estudio, supervisión y reflexión vinculados al psicoanálisis. Considero que el aprendizaje continuo forma parte de la responsabilidad ética del trabajo terapéutico y constituye una herramienta fundamental para sostener una práctica clínica viva, rigurosa y sensible a la complejidad de la experiencia humana.",
    "Entiendo la terapia como un proceso que se construye en conjunto y que puede abrir nuevas posibilidades de comprensión, transformación y encuentro con uno mismo.",
  ],
  experienceTitle: "Mi experiencia",
  experience: [
    "Más de veinte años de clínica",
    "Adultos",
    "Adolescentes",
    "Parejas",
    "Salud",
    "Educación",
  ],
  workTitle: "Mi manera de trabajar",
  work:
    "El psicoanálisis parte de la idea de que no siempre conocemos las razones de aquello que sentimos, pensamos o repetimos. La terapia ofrece un espacio para explorar esas experiencias y comprender los significados que adquieren en la historia singular de cada persona.",
};

export const formacion = {
  items: [
    { title: "Psicóloga", place: "Universidad Andrés Bello" },
    { title: "Magíster en Psicología Clínica", place: "Universidad Andrés Bello" },
    {
      title: "Diplomado en Psicopatología Psicoanalítica",
      place: "Instituto Chileno de Psicoanálisis, ICHPA",
    },
    {
      title: "Analista en Formación",
      place: "Instituto Chileno de Psicoanálisis, ICHPA",
    },
  ],
  note:
    "Entiendo la formación permanente como parte de la responsabilidad ética del trabajo clínico.",
};

export const primeraEntrevista = {
  faqs: [
    {
      q: "¿Qué ocurre en una primera entrevista?",
      a: "La primera entrevista es un espacio de encuentro. Es una oportunidad para que puedas contar, con tus propias palabras, aquello que te trae a consultar. No se trata de un cuestionario ni de una evaluación: es el comienzo de una escucha. Juntos podremos ir comprendiendo qué es lo que te preocupa y de qué manera un proceso terapéutico podría acompañarte.",
    },
    {
      q: "¿Cómo son las sesiones?",
      a: "Las sesiones tienen una duración aproximada de cincuenta minutos y se sostienen en un marco de confidencialidad y respeto. Son un espacio para hablar con libertad, donde la palabra puede desplegarse sin exigencias ni respuestas prefabricadas, favoreciendo un encuentro más profundo con uno mismo.",
    },
    {
      q: "¿Con qué frecuencia?",
      a: "La frecuencia habitual es de una vez por semana, aunque puede ajustarse según el momento y las necesidades de cada proceso. La regularidad ayuda a sostener la continuidad del trabajo y a que aquello que se abre en una sesión pueda seguir elaborándose.",
    },
    {
      q: "¿Modalidad?",
      a: "Atiendo de manera presencial en la consulta ubicada en La Reina y también online, mediante videollamada, para quienes lo prefieran o se encuentren en otra ciudad.",
      modalities: ["Presencial", "Online"],
    },
  ],
};

export const contacto = {
  location: site.location,
  email: site.email,
  whatsapp: site.whatsapp,
};

export const articles = [
  {
    slug: "cuando-las-palabras-encuentran-su-lugar",
    title: "Cuando las palabras comienzan a encontrar su lugar",
    published: true,
    excerpt:
      "¿Qué diferencia puede hacer la psicoterapia frente a todo lo que ya hemos hablado con otros?",
    content: [
      "Hay veces que personas que llegan a consultar me cuentan que han hablado innumerables veces sobre aquello que les preocupa con amigos, familiares o parejas. Han intentado explicarse a sí mismas lo que sienten. Sin embargo, no logran cambiar el malestar que sienten. En este punto surge una pregunta tan cruda como válida: ¿qué diferencia puede hacer la psicoterapia?",
      "Desde una perspectiva psicoanalítica, no toda palabra produce los mismos efectos. Hablar no consiste únicamente en transmitir información acerca de uno mismo; a veces, en el acto mismo de poner una experiencia en palabras, algo nuevo comienza a revelarse. Hay momentos en que creemos saber por qué sufrimos. Tenemos explicaciones, relatos y versiones de nuestra historia; sin embargo, cuando comenzamos a hablar en el espacio psicoterapéutico aparecen asociaciones inesperadas, recuerdos olvidados, emociones contradictorias o sentidos que hasta ese momento habían permanecido ocultos. Hay aspectos de nuestra historia, nuestros deseos, nuestros temores y nuestros conflictos que no siempre conocemos de manera consciente; es por esto que la palabra no cumple solamente una función descriptiva: también puede abrir caminos hacia aquello que aún no ha sido pensado.",
      "Muchas veces el sufrimiento se sostiene porque ciertas experiencias han permanecido aisladas, sin encontrar representación o sin haber sido reconocidas plenamente; otras veces porque una persona ha debido guardar silencio respecto de aspectos importantes de su vida emocional, incluso frente a sí misma. La posibilidad de hablar y ser escuchado en un espacio psicoterapéutico permite que esas experiencias encuentren un lugar distinto. No se trata simplemente de recordar o relatar hechos, sino de construir nuevas formas de comprender lo vivido. Las palabras no cambian el pasado. Sin embargo, pueden transformar la relación que mantenemos con él. Quizás una de las experiencias más significativas de una psicoterapia consiste precisamente en descubrir que algo que parecía confuso, repetitivo o doloroso puede comenzar a adquirir nuevos sentidos cuando encuentra una escucha capaz de alojarlo.",
      "Desde el psicoanálisis entendemos que la palabra no es solamente un medio para comunicar aquello que pensamos. En determinadas condiciones, la palabra puede transformarse en una experiencia que modifica nuestra relación con nosotros mismos, con nuestra historia y con los otros. La psicoterapia ofrece un lugar donde aquello que ha permanecido confuso, silenciado o sin nombre pueda comenzar a encontrar palabras, sentido y reconocimiento.",
    ],
  },
  {
    slug: "la-importancia-de-ser-escuchados",
    title: "La importancia de ser escuchados",
    published: true,
    excerpt:
      "Pocas veces en la vida tenemos la experiencia de ser realmente escuchados.",
    content: [
      "Hay quienes llegan a consultar pensando que necesitan consejos, herramientas o respuestas, una receta con pasos definidos que ayuden a terminar con el malestar que viven. Sin embargo, con frecuencia descubren que lo que más necesitaban era una experiencia de escucha distinta a la que habían encontrado hasta ese momento. Escuchar parece algo simple. Sin embargo, pocas veces en la vida tenemos la experiencia de ser realmente escuchados. Muchas conversaciones cotidianas están llenas de interrupciones, consejos, explicaciones o intentos de resolver rápidamente aquello que nos preocupa. A veces, incluso cuando hablamos de algo importante, sentimos que los demás escuchan desde sus propias ideas, experiencias o expectativas acerca de lo que deberíamos pensar o sentir.",
      "Ser escuchado implica algo diferente. Implica encontrar un espacio donde no es necesario justificar lo que se siente, donde las contradicciones pueden existir y donde aquello que parece confuso puede ser explorado sin apuro. Desde una perspectiva psicoanalítica, la escucha no consiste únicamente en atender a lo que una persona dice. También supone prestar atención a aquello que aparece entre las palabras: silencios, repeticiones, emociones, dudas y preguntas que muchas veces todavía no encuentran una forma clara de expresarse. Cuando la persona habla y es escuchada de una manera distinta, aparecen nuevos significados, conexiones inesperadas y aspectos de nosotros mismos que hasta entonces habían permanecido invisibles.",
      "La experiencia de ser escuchado puede resultar profundamente transformadora. No porque otra persona tenga las respuestas —porque es lo opuesto— sino porque permite construir un espacio donde la propia experiencia puede ser pensada, reconocida y elaborada. En ocasiones, el sufrimiento se vuelve especialmente difícil cuando aquello que hemos vivido no ha encontrado reconocimiento. Cuando nuestras emociones fueron minimizadas, cuando nuestras experiencias fueron cuestionadas o cuando simplemente no hubo un otro disponible para escuchar. Por eso, la escucha tiene un valor que va mucho más allá de la comprensión intelectual. Ser escuchado puede convertirse en una experiencia que restituye algo de la propia voz y de la posibilidad de otorgar sentido a la propia historia. La psicoterapia ofrece un lugar donde la palabra puede desplegarse sin exigencias ni respuestas prefabricadas, favoreciendo un encuentro más profundo con uno mismo y con aquello que necesita ser pensado.",
    ],
  },
  {
    slug: "cuando-una-experiencia-no-encuentra-reconocimiento",
    title: "Cuando una experiencia no encuentra reconocimiento",
    published: true,
    excerpt:
      "No todo sufrimiento proviene únicamente de aquello que nos ocurrió.",
    content: [
      "No todo sufrimiento proviene únicamente de aquello que nos ocurrió. Hay veces en que una parte importante del dolor surge porque lo vivido no encontró un lugar donde pudiera ser comprendido, escuchado o reconocido. Muchas personas han atravesado experiencias difíciles que, por distintas razones, no pudieron ser nombradas en su momento. Otras veces intentaron hablar de lo que les ocurría, pero se encontraron con respuestas que minimizaron su experiencia, la pusieron en duda o simplemente no lograron comprenderla. Cuando esto sucede, el sufrimiento puede adquirir una cualidad particular. No solo se carga con el peso de lo vivido, sino también con la sensación de estar solo frente a ello.",
      "Algunas experiencias quedan suspendidas en el tiempo. No porque sean olvidadas, sino porque no han podido ser integradas plenamente a la propia historia. Permanecen como preguntas sin respuesta, emociones difíciles de elaborar o heridas que continúan produciendo efectos mucho después de que los acontecimientos hayan terminado. Desde una perspectiva psicoanalítica, comprendemos que las experiencias humanas no adquieren significado únicamente por lo que ocurre objetivamente, sino también por la forma en que son vividas, pensadas y reconocidas. La posibilidad de otorgar sentido a una experiencia suele construirse en relación con otros. Por esta razón, la escucha y el reconocimiento ocupan un lugar tan importante en el trabajo terapéutico. No se trata de validar automáticamente todo lo que una persona piensa o siente, sino de crear un espacio donde su experiencia pueda ser acogida, explorada y comprendida en toda su complejidad.",
      "Muchas veces, cuando una experiencia encuentra palabras y una escucha disponible para recibirlas, algo comienza a modificarse. Lo que permanecía aislado puede empezar a integrarse. Lo que aparecía como una vivencia incomprensible puede comenzar a adquirir significado. La psicoterapia no cambia aquello que ocurrió; sin embargo, puede abrir la posibilidad de construir una relación diferente con la propia historia. Porque sanar no consiste en olvidar ni en dejar atrás una experiencia, sino en poder reconocerla, pensarla y encontrar un lugar para ella dentro de la propia vida.",
    ],
  },
  {
    slug: "cuando-el-sufrimiento-insiste-en-repetirse",
    title: "Cuando el sufrimiento insiste en repetirse",
    published: false,
    excerpt: "Sobre aquello que retorna, una y otra vez, buscando ser escuchado.",
  },
  {
    slug: "el-poder-de-la-palabra",
    title: "El poder de la palabra",
    published: false,
    excerpt: "Cómo el acto de hablar puede transformar nuestra relación con la propia historia.",
  },
  {
    slug: "por-que-cuesta-tanto-despedirse",
    title: "¿Por qué cuesta tanto despedirse?",
    published: false,
    excerpt: "Reflexiones sobre la pérdida, el duelo y aquello que insiste en permanecer.",
  },
  {
    slug: "lo-que-el-sintoma-intenta-decir",
    title: "Lo que el síntoma intenta decir",
    published: false,
    excerpt: "El síntoma no solo molesta: también es un mensaje que aún no ha sido descifrado.",
  },
];

export const conversaciones = {
  title: "Conversaciones",
  intro: "Un espacio en construcción para reunir charlas, entrevistas y videos.",
  items: ["Charlas", "Entrevistas", "Videos"],
};

export const images = {
  window:
    "https://images.unsplash.com/photo-1675830028194-02f405ff664b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwd2luZG93JTIwbGlnaHQlMjBzaGFkb3clMjBibGFjayUyMGFuZCUyMHdoaXRlfGVufDB8fHx8MTc4NTE4NjcxNXww&ixlib=rb-4.1.0&q=85",
  room:
    "https://images.unsplash.com/photo-1674731932562-f20b58fbb545?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwdGhlcmFweSUyMGNvbnN1bHRhdGlvbiUyMHJvb20lMjB3YXJtJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzg1MTg2NzE1fDA&ixlib=rb-4.1.0&q=85",
  texture:
    "https://images.unsplash.com/photo-1553326875-1a32421b5e36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHNvZnQlMjBncmFpbnklMjB0ZXh0dXJlJTIwd2FybSUyMG5hdHVyZXxlbnwwfHx8fDE3ODUxODY3MTZ8MA&ixlib=rb-4.1.0&q=85",
};

export const weekdayNames = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

export const monthNames = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
